import {
	type PlacementMessageFrontendFactory,
	type DefaultMessagesResponse,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useEffect, useState, useCallback } from 'react';

import { Inline } from '@atlaskit/primitives/inline';

import { Stack } from '@atlaskit/primitives/stack';

import { usePlacementApi } from '@post-office/use-placement-api';

type CustomDismissCallback = (messageInstanceId: string) => void;

export type placementRenderProps = {
	dismiss: () => void;
	onPrimaryAction?: () => void;
	navigateToLink: string;
	messageInstanceId: string;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: `jira-space-settings-nav-playbooks`,
	options: {
		choreographer: {
			isDefaultEnabled: true, // You almost always will want this option to be true. See below for an explanation
		},
		hideInIsolatedCloud: false, // Set to true if your placement should not be shown in IC
	},
};

const placement = {
	createFrontend: createPlacementFrontend(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	{
		onPrimaryAction?: () => void;
		navigateToLink: string;
	}
> = createFrontend<{
	onPrimaryAction?: () => void;
	navigateToLink: string;
}>((Messages) => (placementProps) => {
	const [messages, setMessages] = useState<DefaultMessagesResponse['messages']>([]);

	const apiResponse = usePlacementApi<DefaultMessagesResponse>('/');

	const { error, data, isLoading } = apiResponse;

	useEffect(() => {
		if (isLoading) {
			return;
		}

		if (error) {
			throw error;
		}
		setMessages(data.messages);
	}, [apiResponse]);

	const handleDismiss: CustomDismissCallback = useCallback(
		(messageInstanceId) => {
			// On dismiss we remove the message from the local state using the messageInstanceId.
			setMessages(messages.filter((message) => messageInstanceId !== message.messageInstanceId));
		},
		[messages],
	);

	return (
		<Stack space="space.150">
			{!isLoading && (
				<Stack as="ul" space="space.200">
					{messages.map((props) => (
						<Inline as="li" key={props.messageInstanceId}>
							<Messages.render
								{...props}
								dismiss={() => handleDismiss(props.messageInstanceId)}
								{...placementProps}
							/>
						</Inline>
					))}
				</Stack>
			)}
		</Stack>
	);
});

export type PlacementFrontendProps = {
	onPrimaryAction?: () => void;
	navigateToLink: string;
};
